import { useState } from 'react'
import { Plus, Settings2, Play, Square } from 'lucide-react'
import { Header } from '@/components/layout/Header'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useFundingStatus, useSaveFundingAlert, useDeleteFundingAlert, useStartFunding, useStopFunding } from '@/hooks/useAlerts'
import { useAuth } from '@/hooks/useAuth'
import { StatsCard } from '@/components/common/StatsCard'
import { ExchangeBadge } from '@/components/common/ExchangeBadge'
import { FundingAlertEditSheet } from '@/components/alerts/FundingAlertEditSheet'
import type { FundingAlert } from '@/types'

export default function Funding() {
  const { data: status, isLoading } = useFundingStatus()
  const saveAlert = useSaveFundingAlert()
  const deleteAlert = useDeleteFundingAlert()
  const startFunding = useStartFunding()
  const stopFunding = useStopFunding()
  const { isAdmin } = useAuth()

  const [editingAlert, setEditingAlert] = useState<FundingAlert | null>(null)
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const alerts = status?.funding_alerts || []
  const isRunning = status?.running ?? true

  const handleAddAlert = () => {
    setEditingAlert(null)
    setIsSheetOpen(true)
  }

  const handleEditAlert = (alert: FundingAlert) => {
    setEditingAlert(alert)
    setIsSheetOpen(true)
  }

  const handleSaveAlert = async (alertData: Partial<FundingAlert>) => {
    await saveAlert.mutateAsync(alertData)
  }

  const handleDeleteAlert = async (id: string) => {
    await deleteAlert.mutateAsync(id)
  }

  return (
    <div className="flex flex-col">
      <Header title="Funding Alerts" />

      <div className="flex-1 space-y-6 p-6">
        {/* Header with Add Alert */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant={isRunning ? 'success' : 'secondary'}>
              {isRunning ? 'Active' : 'Stopped'}
            </Badge>
            {isAdmin && (
              isRunning ? (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => stopFunding.mutate()}
                  disabled={stopFunding.isPending}
                >
                  <Square className="mr-2 h-3 w-3" />
                  {stopFunding.isPending ? 'Stopping...' : 'Stop'}
                </Button>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => startFunding.mutate()}
                  disabled={startFunding.isPending}
                >
                  <Play className="mr-2 h-3 w-3" />
                  {startFunding.isPending ? 'Starting...' : 'Start'}
                </Button>
              )
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleAddAlert}>
              <Plus className="mr-2 h-4 w-4" />
              Add Alert
            </Button>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="border-blue-500/30 bg-blue-500/10">
          <CardContent className="py-4">
            <p className="text-sm text-blue-300">
              <strong>What are Funding alerts?</strong> Monitors funding rates
              across exchanges and alerts when rates exceed configured thresholds.
              Useful for detecting extreme sentiment and potential funding-driven price moves.
            </p>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <StatsCard
            title="Status"
            value={isRunning ? 'Monitoring' : 'Stopped'}
            description="Funding rate monitoring"
            status={isRunning ? 'running' : 'stopped'}
          />
          <StatsCard
            title="Active Triggers"
            value={status?.triggers_count || 0}
            description="Funding alerts triggered"
            status={isRunning ? 'running' : 'stopped'}
          />
          <StatsCard
            title="Configured Alerts"
            value={alerts.length}
            description="Active alert configurations"
          />
        </div>

        {/* Alerts List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Configured Alerts</CardTitle>
            <CardDescription>
              Funding rate monitoring configurations
            </CardDescription>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No funding alerts configured. Click "Add Alert" to create one.
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert, idx) => (
                  <div
                    key={alert.id || idx}
                    className="flex items-center justify-between rounded-lg border p-4"
                  >
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{alert.name || alert.id || `Alert ${idx + 1}`}</span>
                        <Badge variant={alert.enabled !== false ? 'success' : 'secondary'}>
                          {alert.enabled !== false ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(alert.exchanges || []).map((ex) => (
                          <ExchangeBadge key={ex} exchange={ex} />
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Rate threshold: {((alert.funding_rate_threshold || 0) * 100).toFixed(2)}%
                        {alert.time_before_funding_minutes ? ` | ${alert.time_before_funding_minutes}m before funding` : ''}
                        {alert.cooldown_seconds ? ` | Cooldown: ${alert.cooldown_seconds}s` : ''}
                      </p>
                      {/* Tags */}
                      {(alert.tag_expression || (alert.required_tags?.length || 0) > 0 || (alert.exclude_tags?.length || 0) > 0) && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {alert.tag_expression ? (
                            <Badge variant="outline" className="text-xs font-mono">
                              {alert.tag_expression}
                            </Badge>
                          ) : (
                            <>
                              {(alert.required_tags || []).map((tag) => (
                                <Badge key={tag} className="bg-green-500/20 text-green-500 text-xs">
                                  +{tag}
                                </Badge>
                              ))}
                              {(alert.exclude_tags || []).map((tag) => (
                                <Badge key={tag} variant="destructive" className="text-xs">
                                  -{tag}
                                </Badge>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditAlert(alert)}
                      >
                        <Settings2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Sheet */}
      <FundingAlertEditSheet
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        alert={editingAlert}
        onSave={handleSaveAlert}
        onDelete={handleDeleteAlert}
      />
    </div>
  )
}
